import React, { useMemo, useState } from "react";
import { FaTable } from "react-icons/fa6";
import ModalShell, { ModalHeader } from "./ModalShell";
import { CATEGORY_LABELS, COMPARE_FIELDS } from "./constants";
import { formatValue } from "./format";
import { getFieldValue } from "./compare";

// side-by-side comparison of the chosen stages of one record
//   all=false -> only the COMPARE_FIELDS (with per-table aliases)
//   all=true  -> every column of the chosen stages, as stored
export default function CompareModal({ record, stages, onClose, onViewData }) {
  const [all, setAll] = useState(false);

  const allKeys = useMemo(() => {
    const keys = [];
    stages.forEach((st) =>
      Object.keys(st.data || {}).forEach((k) => {
        if (!keys.includes(k)) keys.push(k);
      })
    );
    return keys;
  }, [stages]);

  const rows = useMemo(() => {
    // all fields: raw columns. filtered: configured fields present in >= 1 stage
    const keys = all
      ? allKeys
      : COMPARE_FIELDS.filter((k) =>
        stages.some((st) => getFieldValue(st, k) !== undefined)
      );
    const read = (st, key) =>
      all ? (st.data && key in st.data ? st.data[key] : undefined) : getFieldValue(st, key);

    return keys.map((key) => {
      const values = stages.map((st) => read(st, key));
      const present = values.filter((v) => v !== undefined).map(formatValue);
      const differs =
        present.length < values.length || present.some((v) => v !== present[0]);
      return { key, values, differs };
    });
  }, [stages, all, allKeys]);

  return (
    <ModalShell onClose={onClose} size="modal-xl">
      <ModalHeader
        title={`Compare ${stages.length} stages - ${String(record.id)}`}
        subtitle={`${CATEGORY_LABELS[record.category] || record.category} | ${record.name}`}
        onClose={onClose}
      />

      <div className="modal-body p-0">
        <div className="d-flex justify-content-end align-items-center">
          <div className="form-check form-switch m-3">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="compareAllFields"
              checked={all}
              onChange={(e) => setAll(e.target.checked)}
            />
            <label className="form-check-label small" htmlFor="compareAllFields">
              All fields ({allKeys.length})
            </label>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-sm table-bordered align-middle mb-0 small">
            <thead className="table-light">
              <tr>
                <th>Field</th>
                {stages.map((st) => (
                  <th key={st.stageNum}>
                    {st.stageLabel}
                    <div className="fw-normal text-secondary">{st.stageSearch}</div>
                    {/* <button
                      type="button"
                      className="btn btn-sm btn-outline-primary mt-1"
                      onClick={() => onViewData(st)}
                    >
                      <FaTable className="me-1" /> View Data
                    </button> */}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <td className="fw-semibold">{row.key}</td>
                  {row.values.map((v, i) => (
                    <td
                      key={stages[i].stageNum}
                      className={`text-break ${v === undefined
                          ? "text-secondary"
                          : row.differs
                            ? "table-warning"
                            : ""
                        }`}
                    >
                      {v === undefined ? "-" : formatValue(v)}
                    </td>
                  ))}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={stages.length + 1}
                    className="text-center text-secondary py-3"
                  >
                    None of the configured fields exist in the selected stages.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="modal-footer justify-content-between">
        <span className="small text-secondary">
          <span className="badge text-bg-warning me-2">highlighted</span>
          value differs between the selected stages
        </span>
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Close
        </button>
      </div>
    </ModalShell>
  );
}
